import { forwardRef } from 'react';

import dayjs from 'dayjs';

import { Link as MuiLink } from '@mui/material';

import {
  Column,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerProps,
  Icons,
  LinkBehaviour,
  Typography,
  Box,
  Stack,
  useDrawerInstance,
} from '@cast/design-system';

import { Link } from 'components/router';
import { makeOptimizationWorkloadLink } from 'features/cluster/workload-optimization/navigation';

import {
  OomKillBucketQueryRange,
  useOomKillBucketWorkloadsQuery,
} from '../useOomKillBucketWorkloadsQuery';
import { OomKillBucketWorkload } from '../../types';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoData,
  NoResults,
  Search,
  SearchableTable,
  SearchProvider,
} from '@cast/shared-search-ui';

// Matches Figma drawer timestamp format (comma after date)
const OOM_KILL_DRAWER_DATE_TIME = 'YYYY-MM-DD, HH:mm:ss [GMT]Z';

export type OomKillWorkloadHrefArgs = {
  clusterId: string;
  workloadId: string;
};

export type OomKillWorkloadsDrawerProps = {
  bucketTimestamp: string;
  managed: boolean;
  timeZone: string;
  rangeSelection?: OomKillBucketQueryRange;
  /** Defaults to WOOP workload detail link. */
  getWorkloadHref?: (args: OomKillWorkloadHrefArgs) => string;
};

type FullProps = OomKillWorkloadsDrawerProps & DrawerProps;

const WorkloadNameCell = ({
  workloadName,
  namespace,
  workloadId,
  getWorkloadHref,
}: Pick<OomKillBucketWorkload, 'workloadName' | 'namespace' | 'workloadId'> & {
  getWorkloadHref?: OomKillWorkloadsDrawerProps['getWorkloadHref'];
}) => {
  const { cluster } = useCluster();
  const { close } = useDrawerInstance();
  const href =
    workloadId &&
    (getWorkloadHref?.({ clusterId: cluster.id, workloadId }) ??
      makeOptimizationWorkloadLink({
        clusterId: cluster.id,
        workloadId,
      }));

  return (
    <Stack width="100%" overflow="hidden">
      {href ? (
        <MuiLink
          to={href}
          component={Link}
          underline="none"
          data-testid="oom-kill-workload-link"
          onClick={() => close()}
        >
          <LinkBehaviour variant="P12M" noWrap>
            {workloadName}
          </LinkBehaviour>
        </MuiLink>
      ) : (
        <Typography variant="P12M" noWrap data-testid="oom-kill-workload-name">
          {workloadName}
        </Typography>
      )}
      <Typography variant="P10R" color="grey.400" noWrap>
        {namespace}
      </Typography>
    </Stack>
  );
};

export const OomKillWorkloadsDrawer = forwardRef<HTMLDivElement, FullProps>(
  (
    {
      bucketTimestamp,
      managed,
      timeZone,
      rangeSelection,
      getWorkloadHref,
      ...props
    },
    ref
  ) => {
    const { workloads, isLoading, isError, refetch } =
      useOomKillBucketWorkloadsQuery({
        bucketTimestamp,
        managed,
        rangeSelection,
      });

    const formattedTimestamp = dayjs(bucketTimestamp)
      .tz(timeZone)
      .format(OOM_KILL_DRAWER_DATE_TIME);

    return (
      <Drawer
        ref={ref}
        testId="oom-kill-workloads-drawer"
        size="medium"
        {...props}
      >
        <DrawerHeader />
        <DrawerBody>
          <DrawerContent gap={24}>
            <Stack>
              <Typography
                variant="L12R"
                color="grey.400"
                data-testid="oom-kill-bucket-timestamp"
              >
                {formattedTimestamp}
              </Typography>
              <Typography variant="h6" data-testid="oom-kill-drawer-title">
                {managed
                  ? 'OOMkills - Managed workload'
                  : 'OOMkills - Unmanaged workload'}
              </Typography>
            </Stack>

            {isError ? (
              <FailedToLoad
                title="Failed to load OOM kill workloads"
                refresh={refetch}
              />
            ) : (
              <SearchProvider data={workloads} isLoading={isLoading}>
                <Search testId="oom-kill-workloads-search" />
                <Box mx={-32}>
                  <SearchableTable
                    testId="oom-kill-workloads-table"
                    sizing="m"
                    rowHeight={40}
                    noShadow
                    components={{
                      noResults: (
                        <NoResults
                          icon={<Icons.Question />}
                          entity="workload"
                        />
                      ),
                      noData: (
                        <NoData icon={<Icons.CastNode />} entity="workload" />
                      ),
                    }}
                  >
                    <Column<OomKillBucketWorkload>
                      id="workloadName"
                      header="Workload"
                      minWidth={198}
                      cellWrapperSx={{ pl: 32, pr: 16 }}
                      renderCell={({ workloadName, namespace, workloadId }) => (
                        <WorkloadNameCell
                          workloadName={workloadName}
                          namespace={namespace}
                          workloadId={workloadId}
                          getWorkloadHref={getWorkloadHref}
                        />
                      )}
                      sortable
                    />
                    <Column<OomKillBucketWorkload>
                      id="container"
                      header="Container"
                      minWidth={240}
                      cellWrapperSx={{ px: 16 }}
                      renderCell={({ container }) => (
                        <Typography variant="P12R" noWrap>
                          {container}
                        </Typography>
                      )}
                      sortable
                    />
                    <Column<OomKillBucketWorkload>
                      id="timestamp"
                      header="Timestamp"
                      minWidth={180}
                      cellWrapperSx={{ px: 16 }}
                      renderCell={({ timestamp }) => (
                        <Typography variant="P12R" color="grey.500" noWrap>
                          {dayjs(timestamp)
                            .tz(timeZone)
                            .format(OOM_KILL_DRAWER_DATE_TIME)}
                        </Typography>
                      )}
                      sortable
                    />
                  </SearchableTable>
                </Box>
              </SearchProvider>
            )}
          </DrawerContent>
        </DrawerBody>
      </Drawer>
    );
  }
);

OomKillWorkloadsDrawer.displayName = 'OomKillWorkloadsDrawer';
